import { useState, useEffect } from 'react'
import { X, Loader2 } from 'lucide-react'
import { useCreateCustomer, useCustomerGroupsList } from '@/api/customers'
import { useUIStore } from '@/stores/uiStore'
import type { Customer } from '@/types'

interface AddCustomerModalProps {
  onClose: () => void
  onSuccess: (customer: Customer) => void
}

export function AddCustomerModal({ onClose, onSuccess }: AddCustomerModalProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose() }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [groupId, setGroupId] = useState('')
  // YYYY-MM-DD — what `<input type="date">` emits, and what the BE's
  // birth_date column expects. Optional; cashier can leave blank.
  const [dob, setDob] = useState('')

  const createCustomer = useCreateCustomer()
  const { data: groups = [] } = useCustomerGroupsList()
  const { showToast } = useUIStore()

  // Customer-group is required by the BE Django form. Default the
  // dropdown to the first group as soon as the list resolves so the
  // cashier can submit without manually selecting one. Cashier can
  // still change the choice; we only auto-fill the empty initial state.
  useEffect(() => {
    if (!groupId && groups.length > 0) {
      setGroupId(String(groups[0].id))
    }
  }, [groups, groupId])

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '')
    if (digits.length <= 3) {
      return digits
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setPhone(formatted)
  }

  const hasPhone = phone.replace(/\D/g, '').length >= 10
  const hasEmail = email.trim().length > 0
  const isValid = firstName.trim() && (hasPhone || hasEmail)
  const isSaving = createCustomer.isPending

  const handleSave = async () => {
    if (!isValid) return

    try {
      // Resolve a group_id no matter what — the BE Django form rejects
      // POSTs without one. Prefer the cashier's selection; fall back to
      // the first available group from /select-customer-groups (the same
      // list the dropdown auto-defaulted from).
      const resolvedGroupId = groupId
        ? Number(groupId)
        : (groups[0]?.id ?? undefined)

      const customer = await createCustomer.mutateAsync({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone,
        email: email.trim() || undefined,
        ...(resolvedGroupId != null ? { group_id: resolvedGroupId } : {}),
        // Pass DOB under both names so whichever the BE form
        // recognizes gets persisted. Sent only when the cashier
        // entered a date; an empty string would 400 Django's
        // DateField validator.
        ...(dob ? { birth_date: dob, birthdate: dob } : {}),
      })

      // The cashier-side onSuccess hands the new customer to
      // `useSelectCustomer`, which handles attaching it to the active
      // receipt via PUT /receipts/{id}/customer. We deliberately do
      // not PUT /objects/wholesale-clients/{id} for fields like
      // home_store here — that endpoint 500s on freshly-created
      // customers because the wholesale-clients row doesn't exist
      // yet, and the field isn't required at create time anyway.
      // Set home_store from the full customer-edit form when needed.
      onSuccess(customer)
    } catch {
      showToast({ type: 'error', title: 'Failed to create customer', message: 'Please try again.' })
    }
  }

  const handleReset = () => {
    setFirstName('')
    setLastName('')
    setPhone('')
    setEmail('')
    setGroupId('')
    setDob('')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">New Customer</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* First / Last Name */}
          <div className="grid grid-cols-2 gap-4">
            <FilterSection title="First Name" required>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name"
                className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 focus:bg-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                autoFocus
              />
            </FilterSection>

            <FilterSection title="Last Name">
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name"
                className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 focus:bg-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </FilterSection>
          </div>

          {/* Phone / Email */}
          <div>
            <div className="grid grid-cols-2 gap-4">
              <FilterSection title="Phone Number">
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  maxLength={14}
                  className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 focus:bg-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </FilterSection>

              <FilterSection title="Email">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="customer@email.com"
                  className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 focus:bg-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </FilterSection>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              <span className="text-red-500">*</span> Phone or email is required
            </p>
          </div>

          {/* Date of Birth (optional) | Customer Group (required) */}
          <div className="grid grid-cols-2 gap-4">
            <FilterSection title="Date of Birth">
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                // Sensible upper bound — birth date can't be in the
                // future. Lower bound left open; the BE doesn't care
                // about absurdly old dates and we'd rather not block
                // a valid 1925 birthdate over edge-case validation.
                max={new Date().toISOString().slice(0, 10)}
                className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 focus:bg-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </FilterSection>

            {/* Customer Group — required by the BE create form. Home
                store was removed: setting it on create requires a
                PUT to /objects/wholesale-clients which 500s for
                newly-created customers (the wholesale-clients row
                doesn't exist yet). Cashiers can set it later via
                the full customer-edit form. */}
            <FilterSection title="Customer Group" required>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 focus:bg-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {groups.length === 0 && <option value="">Loading…</option>}
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </FilterSection>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleReset}
            disabled={isSaving}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors cursor-pointer disabled:opacity-50"
          >
            Reset All
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid || isSaving}
            className="flex items-center gap-2 px-6 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors cursor-pointer"
          >
            {isSaving && <Loader2 size={16} className="animate-spin" />}
            Add Customer
          </button>
        </div>
      </div>
    </div>
  )
}

function FilterSection({
  title,
  required,
  children
}: {
  title: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-2">
        {title}
        {required && <span className="text-red-500 ml-1">*</span>}
      </h3>
      {children}
    </div>
  )
}

